import { AWProposalAnalystResponseV4, AWProposalV2, Proposal_TEMP } from '@blk/aw-api-clients';
import { PEWizardCustomDefault, PEWizardStep, ProposalAwaAnalysis } from '@blk/aw-bff-typescript-models';
import { fromPartial } from '@total-typescript/shoehorn';
import { isNil } from 'lodash';
import {
  extractAnalysisFromProposalAnalystForRenderableOutput,
  selectTotalPortfolioAnalysisFromProposalAnalyst,
} from './AnalysisUtils';
import { search } from './JmespathUtils';
import { getPortfolioOrPortfolioGroupFromProposal, isProposalV7 } from './ProposalUtils';

/*
 * This utility function transforms arrays into objects indexed by a specified property
 * This method takes in an array of objects (numerical index) and converts it into an object indexed by
 * the given property
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- Generic type T is needed for array typing
const generateIndexFromPropertyName = <T extends ProposalGeneratorInputs>(arr: T[] | undefined, property: string) =>
  arr?.reduce<ProposalGeneratorInputs>((acc, item) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Dynamic property access is required for this utility
    acc[item[property]] = item;
    return acc;
  }, {}) || {};

/*
 ** TODO This function need to be reviewed when we start supporting InputBox input controls in Instant proposal guides
 *   remove from aw-utils
 */
const DEFAULT_AWA_ANALYSIS: ProposalAwaAnalysis = fromPartial({});
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- existing any, fix later
export type ProposalGeneratorInputs = { [key: string]: any };

export const getDefaultInputBoxValue = (
  customDefault: PEWizardCustomDefault,
  proposalAnalyst: AWProposalAnalystResponseV4 | null,
  portfolioId: string,
  proposal: Proposal_TEMP | AWProposalV2,
  proposalGeneratorInputs: ProposalGeneratorInputs
): string | number | undefined => {
  const { expression, multiplier, sleeve = 'total' } = customDefault;
  // Assume that we are looking at active = false

  let analysis;
  analysis = extractAnalysisFromProposalAnalystForRenderableOutput(
    proposalAnalyst?.analysis.proposalAwaAnalysis ?? DEFAULT_AWA_ANALYSIS,
    portfolioId,
    'original',
    sleeve
  );

  // Get TPA object to enhance analysis with indexed properties
  const totalPortfolioAnalysis = proposalAnalyst?.analysis.proposalAwaAnalysis
    ? selectTotalPortfolioAnalysisFromProposalAnalyst(
        proposalAnalyst.analysis.proposalAwaAnalysis,
        portfolioId,
        'original'
      )
    : undefined;

  // Enhance analysis object with indexed properties (like useDefaultInputValue does in regular guides workflow)
  if (totalPortfolioAnalysis) {
    analysis = {
      ...analysis,
      portfolioID: totalPortfolioAnalysis.portfolioID,
      benchmarkID: totalPortfolioAnalysis.benchmarkID,
      dynamicSleeves: generateIndexFromPropertyName(totalPortfolioAnalysis.dynamicSleeves, 'tag'),
      ruleEvaluations: generateIndexFromPropertyName(totalPortfolioAnalysis.ruleEvaluations, 'ruleName'),
      scores: generateIndexFromPropertyName(totalPortfolioAnalysis.scores, 'scoreName'),
      customColumns: generateIndexFromPropertyName(totalPortfolioAnalysis.customColumns, 'name'),
      activeShare: totalPortfolioAnalysis.activeShare,
      customColumnsSleeves: generateIndexFromPropertyName(totalPortfolioAnalysis.customColumnsSleeves, 'name'),
    };
  }

  const port = isProposalV7(proposal) ? getPortfolioOrPortfolioGroupFromProposal(proposal, portfolioId) : proposal;

  const searchObj = {
    proposal: port ?? proposal,
    analysis,
    parameters: proposalGeneratorInputs,
    inputs: proposalGeneratorInputs,
  };

  let defaultValue;
  try {
    defaultValue = search(searchObj, expression);
  } catch (e) {
    console.warn(
      `The configured customDefault expression ${expression} does not evaluate to a valid value. Please examine configuration.`,
      e
    );
    return undefined;
  }

  if (!isNil(defaultValue)) {
    return multiplier ? Number(defaultValue) * multiplier : (defaultValue as string);
  }
  console.warn(
    `The configured customDefault expression ${expression} does not evaluate to a valid value. Please examine configuration.`
  );
  return undefined;
};

/* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access -- Legacy function accessing untyped props, used across codebase */
// Pending : ticket # 2680776 to fix Proposal Guides Eslint Issues and Refactor 'ProposalGeneratorInputs' Object for Type Safety
export const getAllRulesByConfigNode = (steps: PEWizardStep[], configNode: string) =>
  steps
    .map((step: PEWizardStep) => step.subSteps)
    .flat()
    .map((i) => i.inputs)
    .flat()
    .filter((j) => j.type === 'ALERT_SELECTION')
    .map((k) => k.props[configNode])
    .flat();
